import { PlayerFace } from './PlayerFace';
import { PredictedPointsSparkline } from './PredictedPointsSparkline';
import { PriceChangeIndicator } from './PriceChangeIndicator';
import { TeamBadge } from './TeamBadge';
import { TransferPlayerAction } from './TransferPlayerAction';
import { usePlayerEnrichment } from '@hooks/transfers/usePlayerEnrichment';
import { BodyRegular, CaptionRegular } from '@components/atoms/typography';
import type { TransfersCardMode } from '@lib/transfers/utils/transfers-view-state';
import {
  predictedPointsDelta,
  sumPredictedPoints
} from '@lib/transfers/utils/predicted-points';
import type { TransferRecommendation } from '@api-service/recommendations';

// Grid template shared by the desktop column header and each desktop row:
// sell player, gain, buy player, predicted-points sparkline, affordance.
const DESKTOP_ROW_GRID_CLASS =
  'md:grid-cols-[1fr_148px_1fr_164px_96px] md:gap-4';

const COLUMN_LABEL_CLASS =
  'font-sans text-xs font-bold uppercase tracking-[0.18em]';

/** e.g. "Predicted GW1–6", derived from the recommendation horizon. */
const predictedColumnLabel = (transfers: TransferRecommendation[]) => {
  const gameweeks = (transfers[0]?.in.fixtures ?? []).map(
    (fixture) => fixture.gameweek
  );
  if (gameweeks.length === 0) return 'Predicted';
  const firstGameweek = Math.min(...gameweeks);
  const lastGameweek = Math.max(...gameweeks);
  return firstGameweek === lastGameweek
    ? `Predicted GW${firstGameweek}`
    : `Predicted GW${firstGameweek}–${lastGameweek}`;
};

type SwapPlayerProps = {
  player: TransferRecommendation['in'];
  direction: 'in' | 'out';
  enrichment: ReturnType<typeof usePlayerEnrichment>['getEnrichment'];
  mode: TransfersCardMode;
  isDismissed: boolean;
};

const SwapPlayer = ({
  player,
  direction,
  enrichment,
  mode,
  isDismissed
}: SwapPlayerProps) => {
  const enriched = enrichment(player.id);
  const isOut = direction === 'out';

  return (
    <div
      className={`flex min-w-0 items-center gap-2.5 ${
        isOut ? 'flex-row' : 'flex-row-reverse text-right'
      } ${isDismissed ? 'opacity-45' : ''}`}
    >
      <PlayerFace
        imageUrl={enriched?.imageUrl}
        kitImageUrl={enriched?.kitImageUrl}
        teamShortName={player.team.shortName}
        position={player.position}
        displayName={player.displayName}
        className="h-10 w-10 md:h-11 md:w-11 rounded-full bg-neutral-100 ring-1 ring-neutral-200 flex-shrink-0"
      />
      <div
        className={`flex min-w-0 flex-col gap-[3px] ${
          isOut ? '' : 'items-end'
        }`}
      >
        <div
          className={`flex items-center gap-1 ${
            isOut ? 'flex-row' : 'flex-row-reverse'
          }`}
        >
          <span
            className={`truncate font-open-sans text-sm font-bold text-neutral-900 md:text-base ${
              isDismissed ? 'line-through' : ''
            }`}
          >
            {player.displayName}
          </span>
          <PriceChangeIndicator priceChange={enriched?.priceChange} />
        </div>
        {/* Badge always leads, on both sides, so the two players read the
            same way; only the alignment mirrors. */}
        <div
          className={`flex items-center gap-1 ${isOut ? '' : 'justify-end'}`}
        >
          <TeamBadge
            shortName={player.team.shortName}
            size="xs"
            className="flex-shrink-0"
          />
          <CaptionRegular className="mb-0 whitespace-nowrap text-neutral-500">
            {player.position} · £{player.price.toFixed(1)}m
          </CaptionRegular>
        </div>
        <TransferPlayerAction
          player={player}
          transferType={direction}
          mode={mode}
          isDismissed={isDismissed}
          className={isOut ? 'self-start' : 'self-end'}
        />
      </div>
    </div>
  );
};

type TransferRowProps = {
  transfer: TransferRecommendation;
  index: number;
  enrichment: ReturnType<typeof usePlayerEnrichment>['getEnrichment'];
  isSelected: boolean;
  isSelectable: boolean;
  onSelect?: (index: number) => void;
  mode: TransfersCardMode;
  dismissedPlayerIds: string[];
};

const TransferRow = ({
  transfer,
  index,
  enrichment,
  isSelected,
  isSelectable,
  onSelect,
  mode,
  dismissedPlayerIds
}: TransferRowProps) => {
  const pointsDelta = predictedPointsDelta(transfer);
  const isPositiveDelta = pointsDelta >= 0;
  const deltaTone = isPositiveDelta ? 'text-success-600' : 'text-danger-600';
  const deltaSign = isPositiveDelta ? '+' : '−';
  const buyTotal = sumPredictedPoints(transfer.in.fixtures ?? []);
  const sellTotal = sumPredictedPoints(transfer.out.fixtures ?? []);

  const surfaceClass = isSelected
    ? 'bg-primary-50/50 ring-[1.5px] ring-inset ring-primary-600'
    : 'bg-white ring-1 ring-inset ring-neutral-200 hover:bg-neutral-100';

  const isOutDismissed = dismissedPlayerIds.includes(transfer.out.id);
  const isInDismissed = dismissedPlayerIds.includes(transfer.in.id);

  // The row is a div rather than a button so the per-player dismiss/restore
  // actions can be real buttons inside it.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(index)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.(index);
        }
      }}
      aria-pressed={isSelected}
      className={`grid w-full cursor-pointer grid-cols-2 items-center gap-x-4 gap-y-1.5 rounded-xl p-2.5 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 md:items-center md:p-3 ${DESKTOP_ROW_GRID_CLASS} ${surfaceClass}`}
      data-cy="transfer-row"
      data-cy-index={index}
      data-cy-selected={isSelected}
    >
      <div className="min-w-0 md:order-1">
        <SwapPlayer
          player={transfer.out}
          direction="out"
          enrichment={enrichment}
          mode={mode}
          isDismissed={isOutDismissed}
        />
      </div>

      <div className="min-w-0 md:order-3">
        <SwapPlayer
          player={transfer.in}
          direction="in"
          enrichment={enrichment}
          mode={mode}
          isDismissed={isInDismissed}
        />
      </div>

      <div className="col-span-2 flex items-center justify-center gap-2 md:order-2 md:col-span-1 md:gap-2.5">
        <span
          className={`font-sans text-[15px] font-extrabold tabular-nums md:text-[17px] ${deltaTone}`}
        >
          {deltaSign}
          {Math.abs(pointsDelta).toFixed(1)} pts
        </span>
        {isSelectable && isSelected && (
          <span className="ml-1.5 font-open-sans text-[10px] font-bold uppercase tracking-[0.1em] text-primary-700 md:hidden">
            comparing
          </span>
        )}
      </div>

      <div className="hidden md:order-4 md:flex md:items-center md:justify-end md:gap-2.5">
        <PredictedPointsSparkline
          sellFixtures={transfer.out.fixtures}
          buyFixtures={transfer.in.fixtures}
        />
        <div className="flex flex-col items-end leading-[1.3]">
          <span className="font-sans text-[13px] font-bold tabular-nums text-success-600">
            {buyTotal.toFixed(1)}
          </span>
          <span className="font-sans text-xs font-semibold tabular-nums text-danger-600">
            {sellTotal.toFixed(1)}
          </span>
        </div>
      </div>

      <div className="hidden text-right md:order-5 md:block">
        {isSelectable &&
          (isSelected ? (
            <span className="font-open-sans text-[10px] font-bold uppercase tracking-[0.12em] text-primary-700">
              comparing ▾
            </span>
          ) : (
            <span className="font-open-sans text-xs text-neutral-400">
              compare ▾
            </span>
          ))}
      </div>
    </div>
  );
};

type TransfersListProps = {
  transfers: TransferRecommendation[];
  selectedIndex?: number;
  onSelect?: (index: number) => void;
  mode: TransfersCardMode;
  dismissedPlayerIds: string[];
};

export const TransfersList = ({
  transfers,
  selectedIndex = 0,
  onSelect,
  mode,
  dismissedPlayerIds
}: TransfersListProps) => {
  const playerIds = transfers.flatMap((transfer) => [
    transfer.in.id,
    transfer.out.id
  ]);
  const { getEnrichment } = usePlayerEnrichment(playerIds);

  if (transfers.length === 0) {
    return (
      <BodyRegular className="mb-0 text-neutral-600">
        No transfers to display.
      </BodyRegular>
    );
  }

  const isSelectable = transfers.length > 1;

  return (
    <div className="flex flex-col gap-3" data-cy="transfers-list">
      <div
        className={`grid grid-cols-2 gap-x-4 border-b border-neutral-200 px-3 pb-2 ${DESKTOP_ROW_GRID_CLASS}`}
      >
        <span className={`${COLUMN_LABEL_CLASS} text-danger-600`}>Sell</span>
        <span
          className={`hidden text-center text-neutral-500 md:order-2 md:block ${COLUMN_LABEL_CLASS}`}
        >
          Gain
        </span>
        <span
          className={`text-right text-success-600 md:order-3 ${COLUMN_LABEL_CLASS}`}
        >
          Buy
        </span>
        <span
          className={`hidden whitespace-nowrap text-right text-neutral-500 md:order-4 md:block ${COLUMN_LABEL_CLASS}`}
        >
          {predictedColumnLabel(transfers)}
        </span>
        <span className="hidden md:order-5 md:block" aria-hidden />
      </div>

      <div className="flex flex-col gap-1.5">
        {transfers.map((transfer, index) => (
          <TransferRow
            key={`transfer-${transfer.out.id}-${transfer.in.id}`}
            transfer={transfer}
            index={index}
            enrichment={getEnrichment}
            isSelected={index === selectedIndex}
            isSelectable={isSelectable}
            onSelect={onSelect}
            mode={mode}
            dismissedPlayerIds={dismissedPlayerIds}
          />
        ))}
      </div>
    </div>
  );
};
